import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { Icon, IconType } from '@shared/ui/components/Icon';
import { cn } from '@shared/utils/helpers';

type MenuSectionProps = PropsWithChildren<{
  icon: IconType;
  title: string;
  to?: string;
}>;

export function MenuSection({ icon, title, to, children }: MenuSectionProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const itemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (open) {
      setOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!open && itemRef.current?.querySelector('[aria-current=true]')) {
      setOpen(true);
    }
  });

  if (to) {
    const active = Boolean(to && location.pathname.startsWith(to));

    return (
      <li ref={itemRef}>
        <Link
          to={to}
          className={cn(
            'flex items-center w-full px-6 py-2 text-[#868aa8] hover:bg-[#292b3a] no-underline cursor-pointer',
            {
              'text-[#716aca]': active,
            }
          )}
        >
          <span className="w-8 mr-3 flex items-center justify-center">
            <Icon icon={icon} className="text-2xl" />
          </span>
          <Trans>{title}</Trans>
          <Icon icon="angle-right" className="ml-auto" />
        </Link>
      </li>
    );
  }

  return (
    <li ref={itemRef} data-state={open ? 'open' : 'closed'} className="group/item">
      <button
        className="flex items-center w-full px-6 py-2 text-[#868aa8] hover:bg-[#292b3a] outline-none cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <span className="w-8 mr-3 flex items-center justify-center">
          <Icon icon={icon} className="text-2xl" />
        </span>
        <Trans>{title}</Trans>
        <Icon icon="angle-right" className="ml-auto group-[[data-state=open]]/item:rotate-90" />
      </button>
      <ul className="hidden group-[[data-state=open]]/item:block">{children}</ul>
    </li>
  );
}
